// Resources library: explorer filters (apps/site/components/resource-explorer.tsx), resource pages at
// #<slug> (app/resources/[slug]/page.tsx) and the PDF reader (components/resource-pdf-reader.tsx).
(() => {
  const root = document.querySelector('[data-res-root]');
  if (!root) return;

  const resources = JSON.parse(root.querySelector('[data-res-data]').textContent);
  const library = root.querySelector('[data-res-library]');
  const detail = root.querySelector('[data-res-detail]');
  const baseTitle = document.title;
  let reader = null;

  // ---- Explorer -------------------------------------------------------------
  const cards = [...library.querySelectorAll('[data-res-card]')];
  const search = library.querySelector('[data-res-search]');
  const brandSelect = library.querySelector('[data-res-brand]');
  const productSelect = library.querySelector('[data-res-product]');
  const kindButtons = [...library.querySelectorAll('[data-res-kind]')];
  const count = library.querySelector('[data-res-count]');
  const clear = library.querySelector('[data-res-clear]');
  const grid = library.querySelector('[data-res-grid]');
  const empty = library.querySelector('[data-res-empty]');
  // No "all" view: the first type tab is the default. A search looks across every type.
  const defaultKind = kindButtons[0]?.dataset.resKind ?? '';
  const filters = { query: '', brand: 'All brands', kind: defaultKind, product: 'All products' };

  const renderLibrary = () => {
    const query = filters.query.trim().toLowerCase();
    let shown = 0;
    cards.forEach((card) => {
      const visible =
        (filters.brand === 'All brands' || card.dataset.brand === filters.brand) &&
        (query !== '' || card.dataset.kind === filters.kind) &&
        (filters.product === 'All products' || card.dataset.products.includes(`|${filters.product}|`)) &&
        card.dataset.search.includes(query);
      card.hidden = !visible;
      if (visible) shown += 1;
    });
    kindButtons.forEach((button) => {
      const on = !query && button.dataset.resKind === filters.kind;
      button.classList.toggle('selected', on);
      button.setAttribute('aria-pressed', String(on));
    });
    search.value = filters.query;
    brandSelect.value = filters.brand;
    productSelect.value = filters.product;
    count.textContent = `${shown} ${shown === 1 ? 'resource' : 'resources'}`;
    clear.hidden = !(
      filters.query ||
      filters.brand !== 'All brands' ||
      filters.kind !== defaultKind ||
      filters.product !== 'All products'
    );
    grid.hidden = shown === 0;
    empty.hidden = shown !== 0;
  };

  const reset = () => {
    Object.assign(filters, { query: '', brand: 'All brands', kind: defaultKind, product: 'All products' });
    renderLibrary();
  };

  search.addEventListener('input', () => {
    filters.query = search.value;
    renderLibrary();
  });
  brandSelect.addEventListener('change', () => {
    filters.brand = brandSelect.value;
    renderLibrary();
  });
  productSelect.addEventListener('change', () => {
    filters.product = productSelect.value;
    renderLibrary();
  });
  kindButtons.forEach((button) =>
    button.addEventListener('click', () => {
      filters.kind = button.dataset.resKind;
      filters.query = '';
      renderLibrary();
    }),
  );
  clear.addEventListener('click', reset);
  library.querySelector('[data-res-reset]').addEventListener('click', reset);

  // ---- Resource page --------------------------------------------------------
  const arrow =
    '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M4 12h15m-6-6 6 6-6 6" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"/></svg>';
  const slots = (name) => detail.querySelectorAll(`[data-res-slot="${name}"]`);
  const fill = (name, text) => slots(name).forEach((el) => (el.textContent = text));
  const applicableTo = (resource) => (resource.applicable.length ? resource.applicable : [resource.product]);

  const renderDetail = (resource) => {
    const generation = resource.generation ? ` · ${resource.generation}` : '';
    fill('brand', resource.brand);
    fill('eyebrow', `${resource.brand} · ${resource.kind}${generation}${resource.legacy ? ' · Legacy' : ''}`);
    fill('title', resource.title);
    fill('description', resource.description);
    fill('product', resource.product);

    const download = detail.querySelector('[data-res-download]');
    const open = detail.querySelector('[data-res-open]');
    download.hidden = !resource.file;
    open.hidden = !resource.file;
    download.href = resource.file || '#';
    open.href = resource.file || '#';

    reader?.destroy();
    reader = null;
    const media = detail.querySelector('[data-res-media]');
    media.replaceChildren();
    if (resource.video) {
      const embed = new URL(resource.video, window.location.href);
      embed.searchParams.set('rel', '0');
      embed.searchParams.set('playsinline', '1');
      const frame = document.createElement('iframe');
      frame.src = embed.toString();
      frame.title = resource.title;
      frame.allow = 'fullscreen; picture-in-picture; encrypted-media';
      frame.allowFullscreen = true;
      frame.referrerPolicy = 'strict-origin-when-cross-origin';
      const wrap = document.createElement('div');
      wrap.className = 'resources-video';
      wrap.append(frame);
      media.append(wrap);
    } else if (resource.file) {
      reader = mountReader(media, resource.file, resource.title);
    }

    detail.querySelector('[data-res-fentanyl]').hidden = !/fentanyl/i.test(`${resource.title} ${resource.product}`);

    const products = applicableTo(resource);
    const related = resources.filter(
      (item) =>
        item.slug !== resource.slug &&
        item.brand === resource.brand &&
        applicableTo(item).some((product) => products.includes(product)),
    );
    const list = detail.querySelector('[data-res-related-list]');
    list.replaceChildren(
      ...related.map((item) => {
        const link = document.createElement('a');
        link.href = `#${item.slug}`;
        const text = document.createElement('span');
        const small = document.createElement('small');
        small.textContent = `${item.kind}${item.generation ? ` · ${item.generation}` : ''}`;
        const strong = document.createElement('strong');
        strong.textContent = item.title;
        text.append(small, strong);
        link.append(text);
        link.insertAdjacentHTML('beforeend', arrow);
        return link;
      }),
    );
    detail.querySelector('[data-res-related]').hidden = related.length === 0;

    const shop = root.dataset.shopName;
    document.title = `${resource.title}${generation} · ${resource.brand} – ${shop}`;
  };

  const route = () => {
    const slug = decodeURIComponent(window.location.hash.slice(1));
    const resource = resources.find((item) => item.slug === slug);
    if (resource) {
      renderDetail(resource);
      library.hidden = true;
      detail.hidden = false;
      window.scrollTo(0, 0);
      return;
    }
    reader?.destroy();
    reader = null;
    detail.hidden = true;
    library.hidden = false;
    document.title = baseTitle;
  };

  detail.querySelectorAll('[data-res-home]').forEach((link) =>
    link.addEventListener('click', (event) => {
      event.preventDefault();
      history.pushState(null, '', window.location.pathname + window.location.search);
      route();
      window.scrollTo(0, 0);
    }),
  );
  window.addEventListener('hashchange', route);
  window.addEventListener('popstate', route);

  // ---- PDF reader -----------------------------------------------------------
  let pdfjsPromise = null;
  const loadPdfjs = () => {
    pdfjsPromise ??= import(new URL(root.dataset.pdfLib, window.location.href).href).then((pdfjs) => {
      pdfjs.GlobalWorkerOptions.workerSrc = new URL(root.dataset.pdfWorker, window.location.href).href;
      return pdfjs;
    });
    return pdfjsPromise;
  };

  function mountReader(container, file, title) {
    const element = root.querySelector('[data-res-reader]').content.firstElementChild.cloneNode(true);
    element.setAttribute('aria-label', `${title} reader`);
    container.append(element);
    const $ = (selector) => element.querySelector(selector);
    const prev = $('[data-pdf-prev]');
    const next = $('[data-pdf-next]');
    const select = $('[data-pdf-select]');
    const total = $('[data-pdf-total]');
    const zoomButtons = [...element.querySelectorAll('[data-pdf-zoom]')];
    const status = $('[data-pdf-status]');
    const stage = $('[data-pdf-stage]');
    const pageElement = $('[data-pdf-page]');
    const errorLink = $('[data-pdf-error]');
    const text = $('[data-pdf-text]');
    $('[data-pdf-download]').href = file;
    $('[data-pdf-open]').href = file;
    errorLink.href = file;

    const state = { pdf: null, page: 1, zoom: 1, width: 0, loading: true, error: false };
    let disposed = false;
    let loadingTask = null;
    let renderTask = null;
    let renderId = 0;

    const sync = () => {
      const { pdf, page, zoom, loading, error } = state;
      prev.disabled = !pdf || page <= 1;
      next.disabled = !pdf || page >= pdf.numPages;
      select.disabled = !pdf;
      select.value = String(page);
      total.textContent = `of ${pdf ? pdf.numPages : '…'}`;
      zoomButtons[0].disabled = !pdf || zoom <= 0.75;
      zoomButtons[1].disabled = !pdf;
      zoomButtons[1].textContent = `${Math.round(zoom * 100)}%`;
      zoomButtons[2].disabled = !pdf || zoom >= 2;
      status.textContent = error
        ? 'This document could not be displayed. Open or download the PDF below.'
        : loading
          ? 'Loading document…'
          : `Page ${page} of ${pdf.numPages}`;
      pageElement.style.opacity = loading ? '0.45' : '1';
      errorLink.hidden = !error;
    };

    const fail = () => {
      if (disposed) return;
      state.error = true;
      state.loading = false;
      text.hidden = true;
      sync();
    };

    const renderPage = async () => {
      const { pdf, page, width, zoom } = state;
      if (!pdf || !width) return;
      const id = ++renderId;
      renderTask?.cancel();
      state.loading = true;
      state.error = false;
      text.hidden = true;
      sync();
      try {
        const pdfPage = await pdf.getPage(page);
        if (disposed || id !== renderId) return;
        const original = pdfPage.getViewport({ scale: 1 });
        const viewport = pdfPage.getViewport({ scale: (width / original.width) * zoom });
        const pixelRatio = Math.min(window.devicePixelRatio || 1, 2);
        const canvas = document.createElement('canvas');
        canvas.width = Math.floor(viewport.width * pixelRatio);
        canvas.height = Math.floor(viewport.height * pixelRatio);
        canvas.style.width = `${viewport.width}px`;
        canvas.style.height = `${viewport.height}px`;
        canvas.setAttribute('aria-hidden', 'true');
        renderTask = pdfPage.render({
          canvas,
          viewport,
          transform: pixelRatio === 1 ? undefined : [pixelRatio, 0, 0, pixelRatio, 0, 0],
        });
        const [content] = await Promise.all([pdfPage.getTextContent(), renderTask.promise]);
        if (disposed || id !== renderId) return;
        pageElement.replaceChildren(canvas);
        stage.scrollTo({ top: 0, left: 0 });
        const pageText = content.items
          .map((item) => ('str' in item ? `${item.str}${item.hasEOL ? '\n' : ' '}` : ''))
          .join('');
        text.querySelector('summary').textContent = `Read page ${page} as text`;
        text.querySelector('p').textContent = pageText;
        text.hidden = !pageText.trim();
        state.loading = false;
        sync();
      } catch (error) {
        if (error?.name === 'RenderingCancelledException' || id !== renderId) return;
        fail();
      }
    };

    const goTo = (page) => {
      state.page = page;
      renderPage();
    };

    prev.addEventListener('click', () => goTo(state.page - 1));
    next.addEventListener('click', () => goTo(state.page + 1));
    select.addEventListener('change', () => goTo(Number(select.value)));
    zoomButtons.forEach((button) =>
      button.addEventListener('click', () => {
        const step = Number(button.dataset.pdfZoom);
        state.zoom = step === 0 ? 1 : Math.min(2, Math.max(0.75, state.zoom + step));
        renderPage();
      }),
    );
    stage.addEventListener('keydown', (event) => {
      if (!state.pdf) return;
      if (event.key === 'ArrowRight' && state.page < state.pdf.numPages) {
        event.preventDefault();
        goTo(state.page + 1);
      }
      if (event.key === 'ArrowLeft' && state.page > 1) {
        event.preventDefault();
        goTo(state.page - 1);
      }
    });

    const observer = new ResizeObserver(([entry]) => {
      const width = Math.floor(entry.contentRect.width);
      if (width === state.width) return;
      state.width = width;
      renderPage();
    });
    observer.observe(stage);

    sync();
    loadPdfjs()
      .then((pdfjs) => {
        if (disposed) return null;
        loadingTask = pdfjs.getDocument({ url: file });
        return loadingTask.promise;
      })
      .then((pdf) => {
        if (!pdf || disposed) return;
        state.pdf = pdf;
        select.replaceChildren(
          ...Array.from({ length: pdf.numPages }, (_, index) => new Option(`Page ${index + 1}`, String(index + 1))),
        );
        renderPage();
      })
      .catch(fail);

    return {
      destroy() {
        disposed = true;
        observer.disconnect();
        renderTask?.cancel();
        loadingTask?.destroy();
      },
    };
  }

  renderLibrary();
  route();
})();
